import { NavLink, useLocation } from 'react-router-dom'
import { UserButton, useUser } from '@clerk/clerk-react'
import { cn } from '@/lib/utils'
import { useNotificationCount } from '@/hooks/useNotificationCount'
import {
  LayoutDashboard,
  ListChecks,
  ScrollText,
  Users,
  Waypoints,
  Building2,
  Activity,
  DollarSign,
  MessageCircle,
  X,
} from 'lucide-react'

const NAV_ITEMS = [
  { to: '/dashboard/zeus', icon: MessageCircle, label: 'ZEUS', end: false },
  { to: '/dashboard', icon: LayoutDashboard, label: 'DASHBOARD', end: true },
  { to: '/dashboard/decisions', icon: ListChecks, label: 'DECISIONS', end: false },
  { to: '/dashboard/activity', icon: ScrollText, label: 'ACTIVITY', end: false },
  { to: '/dashboard/agents', icon: Users, label: 'AGENTS', end: false },
  { to: '/dashboard/loom', icon: Waypoints, label: 'LOOM', end: false },
  { to: '/dashboard/customers', icon: Building2, label: 'CUSTOMERS', end: false },
  { to: '/dashboard/costs', icon: DollarSign, label: 'COSTS', end: false },
  { to: '/dashboard/health', icon: Activity, label: 'HEALTH', end: false },
] as const

interface DashboardSidebarProps {
  open: boolean
  onClose: () => void
}

export function DashboardSidebar({ open, onClose }: DashboardSidebarProps) {
  const location = useLocation()
  const { user } = useUser()
  const { pendingDecisions, total } = useNotificationCount()
  const displayName =
    user?.fullName ||
    user?.primaryEmailAddress?.emailAddress ||
    user?.username ||
    ''

  const sidebar = (
    <aside
      className={cn(
        'fixed left-0 top-0 z-50 flex h-screen w-64 flex-col bg-cmd-low transition-transform duration-200',
        'motion-reduce:transition-none',
        'lg:translate-x-0',
        open ? 'translate-x-0' : '-translate-x-full'
      )}
      role="navigation"
      aria-label="Dashboard navigation"
    >
      {/* Logo */}
      <div className="flex h-16 items-center justify-between px-6">
        <div className="flex items-center gap-2">
          <span className="font-sans text-lg font-bold tracking-tight text-white uppercase">
            TMAgent
          </span>
          {total > 0 && (
            <span
              className="flex h-5 min-w-5 items-center justify-center rounded-full bg-cmd-error px-1.5 font-mono text-[0.6rem] font-bold tabular-nums text-white"
              aria-label={`${total} items need attention`}
              title={`${total} items need attention`}
            >
              {total > 99 ? '99+' : total}
            </span>
          )}
        </div>
        <button
          className="lg:hidden p-1.5 -mr-1.5 text-cmd-outline hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          onClick={onClose}
          aria-label="Close navigation menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Nav links */}
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onClose}
            aria-current={location.pathname === item.to || (!item.end && location.pathname.startsWith(item.to)) ? 'page' : undefined}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2.5 font-label text-xs tracking-wider transition-colors duration-150',
                'motion-reduce:transition-none',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary',
                isActive
                  ? 'border-l-2 border-primary text-primary font-medium'
                  : 'border-l-2 border-transparent text-cmd-outline hover:text-white'
              )
            }
          >
            <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{item.label}</span>
            {item.to === '/dashboard/decisions' && pendingDecisions > 0 && (
              <span
                className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-cmd-error px-1.5 font-mono text-[0.6rem] font-bold tabular-nums text-white"
                aria-label={`${pendingDecisions} pending`}
              >
                {pendingDecisions > 99 ? '99+' : pendingDecisions}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User section */}
      <div className="px-3 py-4">
        <div className="flex items-center gap-3 px-3 py-2">
          <UserButton
            afterSignOutUrl="/sign-in"
            appearance={{ elements: { avatarBox: 'h-8 w-8' } }}
          />
          <span className="flex-1 truncate font-label text-xs text-cmd-outline">
            {displayName}
          </span>
        </div>
      </div>
    </aside>
  )

  return (
    <>
      {sidebar}

      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden motion-reduce:transition-none"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
    </>
  )
}
